import { Injectable, computed, inject, signal } from '@angular/core';
import { TOUR_CONFIG } from './tour.config';
import type { TourStepRegistration } from './tour.types';

@Injectable({ providedIn: 'root' })
export class TourService {
  private readonly config = inject(TOUR_CONFIG);
  private readonly steps = new Map<string, TourStepRegistration>();
  private readonly _active = signal(false);
  private readonly _tourId = signal<string | null>(null);
  private readonly _currentIndex = signal(0);
  private readonly _orderedIds = signal<string[]>([]);
  private readonly _registryVersion = signal(0);

  readonly active = this._active.asReadonly();
  readonly tourId = this._tourId.asReadonly();
  readonly currentIndex = this._currentIndex.asReadonly();
  /** Bumps when steps are registered or unregistered (for reactive availability checks). */
  readonly registryVersion = this._registryVersion.asReadonly();

  readonly orderedSteps = computed(() =>
    this._orderedIds()
      .map((id) => this.steps.get(id))
      .filter((step): step is TourStepRegistration => step != null),
  );

  readonly currentStep = computed(() => {
    const ids = this._orderedIds();
    const index = this._currentIndex();
    const id = ids[index];
    return id ? (this.steps.get(id) ?? null) : null;
  });

  readonly stepCount = computed(() => this._orderedIds().length);

  readonly isFirst = computed(() => this._currentIndex() <= 0);

  readonly isLast = computed(
    () => this._currentIndex() >= this._orderedIds().length - 1,
  );

  register(step: TourStepRegistration): void {
    const existing = this.steps.get(step.id);
    const unchanged =
      existing &&
      existing.tourId === step.tourId &&
      existing.order === step.order &&
      existing.title === step.title &&
      existing.content === step.content &&
      existing.element === step.element;

    this.steps.set(step.id, step);

    if (!unchanged) {
      this._registryVersion.update((v) => v + 1);
    }
  }

  unregister(id: string): void {
    if (!this.steps.delete(id)) {
      return;
    }
    this._registryVersion.update((v) => v + 1);
    if (this._active()) {
      const currentStepId = this._orderedIds()[this._currentIndex()];
      const ids = this._orderedIds().filter((stepId) => stepId !== id);
      this._orderedIds.set(ids);
      if (ids.length === 0) {
        this.end();
        return;
      }
      if (currentStepId && currentStepId !== id) {
        const preservedIndex = ids.indexOf(currentStepId);
        if (preservedIndex >= 0) {
          this._currentIndex.set(preservedIndex);
          return;
        }
      }
      if (this._currentIndex() >= ids.length) {
        this._currentIndex.set(Math.max(0, ids.length - 1));
      }
    }
  }

  /** Start a guided tour. Steps are sorted by `order`, optionally filtered by `tourId`. */
  start(tourId?: string, startAtStepId?: string): void {
    const ids = this.buildOrder(tourId);
    if (ids.length === 0) return;

    let index = 0;
    if (startAtStepId) {
      const found = ids.indexOf(startAtStepId);
      if (found >= 0) index = found;
    }

    this._tourId.set(tourId ?? null);
    this._orderedIds.set(ids);
    this._currentIndex.set(index);
    this._active.set(true);
    this.scrollCurrentIntoView();
  }

  /** Highlight a single step without walking through the full tour. */
  highlight(stepId: string): void {
    if (!this.steps.has(stepId)) return;
    this._tourId.set(null);
    this._orderedIds.set([stepId]);
    this._currentIndex.set(0);
    this._active.set(true);
    this.scrollCurrentIntoView();
  }

  next(): void {
    if (!this._active()) return;
    if (this.isLast()) {
      this.end(true);
      return;
    }
    this._currentIndex.update((i) => i + 1);
    this.scrollCurrentIntoView();
  }

  prev(): void {
    if (!this._active() || this.isFirst()) return;
    this._currentIndex.update((i) => i - 1);
    this.scrollCurrentIntoView();
  }

  goTo(stepId: string): void {
    const ids = this._orderedIds();
    const index = ids.indexOf(stepId);
    if (index < 0) return;
    this._currentIndex.set(index);
    this.scrollCurrentIntoView();
  }

  end(markCompleted = false): void {
    const tourId = this._tourId();
    if (markCompleted && tourId) {
      this.markCompleted(tourId);
    }
    this._active.set(false);
    this._tourId.set(null);
    this._orderedIds.set([]);
    this._currentIndex.set(0);
  }

  isCompleted(tourId: string): boolean {
    try {
      const raw = localStorage.getItem(this.storageKey(tourId));
      return raw === '1';
    } catch {
      return false;
    }
  }

  markCompleted(tourId: string): void {
    try {
      localStorage.setItem(this.storageKey(tourId), '1');
    } catch {
      /* ignore quota / private mode */
    }
  }

  resetCompleted(tourId: string): void {
    try {
      localStorage.removeItem(this.storageKey(tourId));
    } catch {
      /* ignore */
    }
  }

  getSpotlightPadding(): number {
    return this.config.spotlightPadding ?? 8;
  }

  getBackdropOpacity(): number {
    return this.config.backdropOpacity ?? 0.72;
  }

  getZIndex(): number {
    return this.config.zIndex ?? 10100;
  }

  hasStepsForTour(tourId: string): boolean {
    for (const step of this.steps.values()) {
      if (step.tourId === tourId) {
        return true;
      }
    }
    return false;
  }

  private refreshOrder(tourId?: string): void {
    const currentStepId = this._orderedIds()[this._currentIndex()];
    const ids = this.buildOrder(tourId);

    if (this.sameStepOrder(this._orderedIds(), ids)) {
      return;
    }

    this._orderedIds.set(ids);

    if (currentStepId) {
      const preservedIndex = ids.indexOf(currentStepId);
      if (preservedIndex >= 0) {
        if (preservedIndex !== this._currentIndex()) {
          this._currentIndex.set(preservedIndex);
        }
        return;
      }
    }

    if (this._currentIndex() >= ids.length) {
      this._currentIndex.set(Math.max(0, ids.length - 1));
    }
  }

  private sameStepOrder(current: string[], next: string[]): boolean {
    return (
      current.length === next.length &&
      current.every((id, index) => id === next[index])
    );
  }

  private buildOrder(tourId?: string): string[] {
    const entries = [...this.steps.values()]
      .filter((step) => !tourId || step.tourId === tourId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return entries.map((step) => step.id);
  }

  private scrollCurrentIntoView(): void {
    const el = this.currentStep()?.element;
    if (!el?.scrollIntoView) return;
    queueMicrotask(() => {
      el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'auto' });
    });
  }

  private storageKey(tourId: string): string {
    return `${this.config.storageKey ?? 'tc_tour_completed'}:${tourId}`;
  }
}
